import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'

function WorkoverHistory() {
    const { theme } = useTheme()
    const navigate = useNavigate()
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [filterWell, setFilterWell] = useState('')
    const [filterYear, setFilterYear] = useState('')

    useEffect(() => { fetchData() }, [])

    const fetchData = async () => {
        setLoading(true)
        try {
            const params = new URLSearchParams()
            if (filterWell) params.set('well', filterWell)
            if (filterYear) params.set('year', filterYear)
            const qs = params.toString() ? `?${params}` : ''
            const res = await fetch(`${apiConfig.baseUrl}/workovers/history${qs}`)
            if (!res.ok) throw new Error('Failed to fetch history')
            setData(await res.json())
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    const uniqueWells = [...new Set(data.map(j => j.wellName))].sort()
    const uniqueYears = [...new Set(data.map(j => j.startDate ? new Date(j.startDate).getFullYear() : null).filter(Boolean))].sort((a, b) => b - a)

    const filtered = data.filter(j => {
        if (filterWell && j.wellName !== filterWell) return false
        if (filterYear && (!j.startDate || new Date(j.startDate).getFullYear() !== parseInt(filterYear))) return false
        return true
    })

    const totalCost = filtered.reduce((sum, j) => sum + (j.totalCost || 0), 0)

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className={`border-b-2 ${theme === 'dark' ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <button onClick={() => navigate('/workovers')}
                    className={`text-sm mb-2 inline-block ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}>
                    &larr; Back to Workovers
                </button>
                <h1 className={`text-xl sm:text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                    <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                    Workover History
                </h1>
                <p className={`${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 ml-4`}>
                    {filtered.length} workovers &middot; ${totalCost.toLocaleString()} total cost
                </p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
                <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
                    className={`px-3 py-2 rounded-lg text-sm ${
                        theme === 'dark'
                            ? 'bg-slate-800 border-slate-700 text-gray-200'
                            : 'bg-white border-gray-300 text-gray-900'
                    } border`}>
                    <option value="">All Years</option>
                    {uniqueYears.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select value={filterWell} onChange={e => setFilterWell(e.target.value)}
                    className={`px-3 py-2 rounded-lg text-sm ${
                        theme === 'dark'
                            ? 'bg-slate-800 border-slate-700 text-gray-200'
                            : 'bg-white border-gray-300 text-gray-900'
                    } border`}>
                    <option value="">All Wells</option>
                    {uniqueWells.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
            </div>

            {/* History Table */}
            {loading ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aviator-600"></div>
                </div>
            ) : filtered.length > 0 ? (
                <div className="chart-container overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Well</th>
                                <th>Job Type</th>
                                <th>Objective</th>
                                <th>Start</th>
                                <th>End</th>
                                <th className="text-right">Days</th>
                                <th className="text-right">Cost</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((j, i) => (
                                <tr key={j.jobId || i}
                                    className={j.jobId ? 'cursor-pointer' : ''}
                                    onClick={() => j.jobId && navigate(`/workovers/detail/sf-${j.jobId}`)}>
                                    <td className="font-medium">
                                        {j.wellName}
                                        {j.jobId && <span className={`ml-2 text-xs ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>&rarr;</span>}
                                    </td>
                                    <td>{j.jobType || '--'}</td>
                                    <td className={`max-w-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {j.objective || '--'}
                                    </td>
                                    <td>{j.startDate ? new Date(j.startDate).toLocaleDateString() : '--'}</td>
                                    <td>{j.endDate ? new Date(j.endDate).toLocaleDateString() : '--'}</td>
                                    <td className="text-right">{j.durationDays != null ? `${j.durationDays}d` : '--'}</td>
                                    <td className="text-right font-mono">
                                        {j.totalCost != null ? `$${j.totalCost.toLocaleString()}` : '--'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>No workovers found</p>
            )}
        </div>
    )
}

export default WorkoverHistory
